import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../supabaseClient';
import { parseLocalDate } from '../../../utils/dateUtils';

const UpcomingDeadlines = () => {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    const fetchDeadlines = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];

        const { data, error } = await supabase
          .from('school_deadlines')
          .select('id, school_name, deadline_date, event_type, article_title, article_url')
          .gte('deadline_date', today)
          .eq('is_expired', false)
          .order('deadline_date', { ascending: true })
          .limit(4);

        if (error) throw error;

        const processed = (data || []).map(item => ({
          id:           item.id,
          title:        item.school_name,
          description:  item.article_title,
          date:         item.deadline_date,
          type:         item.event_type || 'general',
          url:          item.article_url,
          daysLeft:     Math.ceil(
            (parseLocalDate(item.deadline_date) - new Date()) / (1000 * 60 * 60 * 24)
          ),
          priority:
            Math.ceil((parseLocalDate(item.deadline_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 7
              ? 'high'
              : Math.ceil((parseLocalDate(item.deadline_date) - new Date()) / (1000 * 60 * 60 * 24)) <= 30
              ? 'medium' : 'low',
        }));

        setDeadlines(processed);
      } catch (error) {
        console.warn('UpcomingDeadlines — erreur Supabase :', error.message);
        setDeadlines([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDeadlines();
  }, []);

  const getTypeIcon = (type) => {
    switch (type) {
      case 'inscription': return 'UserPlus';
      case 'concours':    return 'FileText';
      case 'bourse':      return 'Award';
      case 'résultats':   return 'CheckCircle';
      default:            return 'Calendar';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'inscription': return 'text-blue-600 bg-blue-50';
      case 'concours':    return 'text-red-600 bg-red-50';
      case 'bourse':      return 'text-purple-600 bg-purple-50';
      case 'résultats':   return 'text-green-600 bg-green-50';
      default:            return 'text-gray-600 bg-gray-50';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':   return 'border-l-red-500';
      case 'medium': return 'border-l-yellow-500';
      default:       return 'border-l-green-500';
    }
  };

  const formatDate = (dateString) =>
    parseLocalDate(dateString)?.toLocaleDateString('fr-FR', {
      day: 'numeric', month: 'long', year: 'numeric',
    });

  // ── État vide : pas de deadlines en base ─────────────────────────────────
  if (!loading && deadlines.length === 0) {
    return (
      <section className="py-16 bg-gradient-to-br from-red-50 to-orange-50">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
            Calendrier des Concours
          </h2>
          <p className="text-muted-foreground mb-6">
            Aucune échéance à venir pour le moment. Lancez le scraper pour alimenter le calendrier.
          </p>
          <Link
            to="/calendrier-admissions"
            className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-medium"
          >
            <span>Voir le calendrier</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-br from-red-50 to-orange-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Calendrier des Concours
            </h2>
            <p className="text-lg text-muted-foreground">
              Les dates clés à ne pas rater pour la session {new Date().getFullYear()}
            </p>
          </div>
          <Link
            to="/calendrier-admissions"
            className="hidden lg:flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
          >
            <span className="font-medium">Voir le calendrier complet</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-card rounded-2xl p-6 shadow-soft border-l-4 border-l-gray-200 animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-3" />
                <div className="h-3 bg-gray-100 rounded w-full mb-2" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {deadlines.map(deadline => (
              <div
                key={deadline.id}
                className={`bg-card rounded-2xl p-6 shadow-soft border-l-4 ${getPriorityColor(deadline.priority)} hover:shadow-interactive transition-shadow`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getTypeColor(deadline.type)}`}>
                      <Icon name={getTypeIcon(deadline.type)} size={20} />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground">
                        {deadline.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(deadline.date)}
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <div className={`text-2xl font-bold ${deadline.daysLeft <= 10 ? 'text-red-600' : 'text-primary'}`}>
                      {deadline.daysLeft > 0 ? deadline.daysLeft : 'J-0'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {deadline.daysLeft <= 0
                        ? "Aujourd'hui"
                        : deadline.daysLeft === 1
                        ? 'jour restant'
                        : 'jours restants'}
                    </div>
                  </div>
                </div>

                <p className="text-muted-foreground mb-4 leading-relaxed text-sm line-clamp-2">
                  {deadline.description}
                </p>

                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <Link
                    to="/calendrier-admissions"
                    className="text-sm text-primary hover:text-primary/80 transition-colors font-medium flex items-center gap-1"
                  >
                    <span>Calendrier complet</span>
                    <Icon name="ArrowRight" size={14} />
                  </Link>
                  {deadline.url && (
                    <a
                      href={deadline.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name="ExternalLink" size={14} />
                      <span>Source</span>
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA mobile */}
        <div className="text-center mt-8 lg:hidden">
          <Link
            to="/calendrier-admissions"
            className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
          >
            <span className="font-medium">Voir le calendrier complet</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default UpcomingDeadlines;
